import React from "react";
import {
  SingleStepForm,
  MultiStepForm,
  Header,
  HeaderActionProps,
} from "@tomis-tech/chat-ui";

import { ChatWidgetConfiguration, FormInstance } from "@/types";
import { Toast } from "@/components/Toast";
import { useToast } from "@/hooks";
import {
  FormOnlyContainer,
  ErrorStateContainer,
  ErrorIcon,
  ErrorTitle,
  ErrorMessage,
} from "./styled";

export interface FormOnlyOpenProps {
  /** Chat widget configuration for branding */
  config: Pick<ChatWidgetConfiguration, "name" | "image">;
  /** Whether widget is in embed mode */
  embed: boolean;
  /** The active form to display */
  activeForm: FormInstance | null;
  /** Whether form submission is in progress */
  isSubmitting: boolean;
  /** Update form field values */
  updateFormValues: (patch: Record<string, any>) => void;
  /** Submit the current form */
  submitForm: () => Promise<boolean>;
  /** Close/minimize the widget */
  onClose: () => void;
  /** Whether the form can be dismissed (close button shown) */
  dismissible: boolean;
}

/**
 * The open view for form-only mode.
 *
 * Displays only a form (no chat interface) with optional header and footer.
 * Shows toast notifications for success/error states.
 */
export const FormOnlyOpen: React.FC<FormOnlyOpenProps> = ({
  config,
  embed,
  activeForm,
  isSubmitting,
  updateFormValues,
  submitForm,
  onClose,
  dismissible,
}) => {
  const {
    message: toastMessage,
    visible: toastVisible,
    variant: toastVariant,
    hideToast,
  } = useToast();

  // Build header actions - only show close button if not embedded and dismissible
  const headerActions: HeaderActionProps[] = [];
  if (!embed && dismissible) {
    headerActions.push({
      icon: "minus",
      size: 16,
      onClick: onClose,
      ariaLabel: "Minimize form",
    });
  }

  // No form available - show error state
  if (!activeForm) {
    return (
      <FormOnlyContainer>
        <Header
          title={config.name}
          image={config.image}
          actions={headerActions}
        />
        <ErrorStateContainer>
          <ErrorIcon>!</ErrorIcon>
          <ErrorTitle>Form Unavailable</ErrorTitle>
          <ErrorMessage>
            This form is not currently available. Please try again later or
            contact us for assistance.
          </ErrorMessage>
        </ErrorStateContainer>
      </FormOnlyContainer>
    );
  }

  // Extract form data
  const formValues = activeForm.values ?? {};
  const formSteps = activeForm.schema.steps ?? [];
  const formFields = activeForm.schema.fields ?? [];

  // For form-only mode, we render the form overlay directly in the container.
  // The SingleStepForm/MultiStepForm components use FormOverlay which is an
  // absolutely positioned overlay that fills its container (position: absolute, inset: 0).
  // FormOnlyContainer has position: relative to contain the overlay properly.
  return (
    <FormOnlyContainer>
      {activeForm.schema.mode === "multi-step" ? (
        <MultiStepForm
          steps={formSteps}
          values={formValues}
          onChange={updateFormValues}
          onClose={onClose}
          onSubmit={submitForm}
          busy={isSubmitting}
          dismissible={dismissible}
        />
      ) : (
        <SingleStepForm
          title={activeForm.schema.title}
          subtitle={activeForm.schema.subtitle}
          instructions={activeForm.schema.instructions}
          onClose={onClose}
          onSubmit={submitForm}
          busy={isSubmitting}
          fields={formFields}
          onChange={updateFormValues}
          values={formValues}
          buttonLabel={activeForm.schema.submitLabel}
          dismissible={dismissible}
        />
      )}
      <Toast
        message={toastMessage}
        visible={toastVisible}
        onClose={hideToast}
        variant={toastVariant}
      />
    </FormOnlyContainer>
  );
};
